import { array, record, string } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { find, get, getOr, isEmpty, negate, pickBy } from 'lodash/fp';
import { createSelector } from 'reselect';

import { ServiceType, TimeUnit } from '@mews/enums';
import { getOrEmptyString, getStringProp, values } from '@mews/fp';
import { matchTimeUnitKey, MatchTimeUnitModifiers } from '@mews/framework/localization';
import { createPropsExtender } from '@mews/framework/redux';
import { TextKey } from '@mews-distributor/common';

import { ApplicationState } from '../../../types';
import { WithServiceId } from '../../../types/selectors/selectorPropTypes';
import { getEntities } from '../getEntities';
import { isEndOffsetNegative } from './dateUtils';
import { BookableService, BookableServices, Service, Services } from './types';

const getServices = createSelector(getEntities, (entities) => entities.services ?? {});

const getService = createSelector<ApplicationState, WithServiceId, string, Services, Service | undefined>(
    getStringProp('serviceId'),
    getServices,
    get
);

export const getServiceEnterpriseId = createSelector(getService, get('enterpriseId'));
export const serviceEnterpriseIdPropsExtender = createPropsExtender('enterpriseId', getServiceEnterpriseId);

const bookableServiceTypeGuard = (service: Service): service is BookableService =>
    service.data.discriminator === ServiceType.Bookable;
export const getBookableServices = createSelector<ApplicationState, Services, BookableServices>(
    getServices,
    pickBy(bookableServiceTypeGuard)
);

const getEnterpriseService = createSelector(
    getStringProp('enterpriseId'),
    getBookableServices,
    (enterpriseId, services) => find({ enterpriseId }, services)
);

export const getEnterpriseServiceId = flow(getEnterpriseService, get('id'));
export const enterpriseServiceIdPropsExtender = createPropsExtender('serviceId', getEnterpriseServiceId);

const getServiceData = createSelector(getEnterpriseService, get('data'));

const getServiceDataValue = createSelector(getServiceData, get('value'));

export const getServiceTimeUnit = createSelector(getServiceDataValue, getOr(TimeUnit.Day, 'timeUnit'));

const getServiceEndOffset = createSelector(getServiceDataValue, getOrEmptyString('endOffset'));

const isDayUse = (timeUnit: TimeUnit, endOffset: string) =>
    timeUnit === TimeUnit.Hour || (timeUnit === TimeUnit.Day && isEndOffsetNegative(endOffset));

export const isServiceDayUse = createSelector(getServiceTimeUnit, getServiceEndOffset, isDayUse);
export const isServiceHourly = createSelector(getServiceTimeUnit, (timeUnit) => timeUnit === TimeUnit.Hour);

export const getDayUseServices = createSelector<ApplicationState, BookableServices, BookableServices>(
    getBookableServices,
    pickBy((service) => isDayUse(service.data.value.timeUnit, service.data.value.endOffset))
);

export const getDayUseServiceEnterpriseIds = createSelector(getDayUseServices, (dayUseServices) =>
    pipe(
        dayUseServices,
        record.map((service) => service.enterpriseId),
        values,
        array.uniq(string.Eq)
    )
);

const getEnterpriseDayUseServices = createSelector(
    getStringProp('enterpriseId'),
    getDayUseServices,
    (enterpriseId, services) => pickBy({ enterpriseId }, services)
);

export const hasEnterpriseDayUseServices = createSelector(getEnterpriseDayUseServices, negate(isEmpty));

export const getServiceTimeUnitKey = (modifiers: MatchTimeUnitModifiers = {}) =>
    createSelector(isServiceDayUse, isServiceHourly, getServiceTimeUnit, (dayUse, isHourlyService, timeUnit) =>
        matchTimeUnitKey({
            ...modifiers,
            timeUnit,
            isServiceDayUse: dayUse,
            isHourlyService,
        })
    );

export const getServiceBookablePeriodTextKey = createSelector(isServiceDayUse, (hasDayUse) =>
    hasDayUse ? TextKey.SubstitutableDayPlural : TextKey.SubstitutableNightPlural
);

export const getServiceTermsAndConditionsUrl = flow(getService, (service) => service?.data.value.termsAndConditionsUrl);
export const getServicePrivacyPolicyUrl = flow(getService, (service) => service?.data.value.privacyPolicyUrl);
